import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { useFetchStateHelper, useMergedFetchState } from "hooks/useFetchState";

export const useLabels = () => {
  const { data: labels, ...queryResponse } = useQuery({
    queryKey: ["labels"],
    queryFn: api.labels.availableLabels,
  });

  const { fetchState } = useFetchStateHelper(queryResponse);

  return {
    labels,
    fetchState,
  };
};

export const useLabelsActions = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: createLabel, ...createResponse } = useMutation({
    mutationKey: ["create-label"],
    mutationFn: ({ labelData, users, fields }) =>
      api.labels.createLabel({ labelData, userIds: users, fieldIds: fields }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
  const { fetchState: createFetchState } = useFetchStateHelper(createResponse);

  const { mutateAsync: updateLabel, ...updateResponse } = useMutation({
    mutationKey: ["update-label"],
    mutationFn: ({ id, payload }) => api.labels.updateLabel(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
  const { fetchState: updateFetchState } = useFetchStateHelper(updateResponse);

  const { mutateAsync: deleteLabel, ...deleteResponse } = useMutation({
    mutationKey: ["delete-label"],
    mutationFn: (id) => api.labels.deleteLabel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
  const { fetchState: deleteFetchState } = useFetchStateHelper(deleteResponse);

  const { mutateAsync: addLabelToResource, ...addResponse } = useMutation({
    mutationKey: ["add-label"],
    mutationFn: ({ id, type, resourceId }) =>
      api.labels.addLabelToResource(id, type, resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
  const { fetchState: addFetchState } = useFetchStateHelper(addResponse);

  const { mutateAsync: removeLabelFromResource, ...removeResponse } =
    useMutation({
      mutationKey: ["remove-label"],
      mutationFn: ({ id, type, resourceId }) =>
        api.labels.removeLabelFromResource(id, type, resourceId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["labels"] });
      },
    });
  const { fetchState: removeFetchState } = useFetchStateHelper(removeResponse);

  const { fetchState } = useMergedFetchState([
    createFetchState,
    updateFetchState,
    deleteFetchState,
    addFetchState,
    removeFetchState,
  ]);
  return {
    createLabel,
    updateLabel,
    deleteLabel,
    addLabelToResource,
    removeLabelFromResource,
    fetchState,
  };
};
